import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { PopupHttpErrors } from 'src/app/decorators/http-error/popup-http-errors.decorator';
import { IAcademicStatus } from 'src/app/shared/models/academic-status.model';
import { IAddress } from 'src/app/shared/models/address.model';
import { IAdmissionsApplication, IFullAdmissionsApplication } from 'src/app/shared/models/admissions-application.model';
import { IAdmissionsDeposit } from 'src/app/shared/models/admissions-deposit.model';
import { IAdmissionsQuestion } from 'src/app/shared/models/admissions-question.model';
import { IAdvisor } from 'src/app/shared/models/advisor.model';
import { IAuditCourseHistory } from 'src/app/shared/models/audit-course-history.model';
import { IBio } from 'src/app/shared/models/bio.model';
import { ICitizenship } from 'src/app/shared/models/citizenship.model';
import { IComment } from 'src/app/shared/models/comment.model';
import { IConcealedCarryStatus } from 'src/app/shared/models/concealed-carry-status.model';
import { IDescription } from 'src/app/shared/models/description.model';
import { IFerpaPassword } from 'src/app/shared/models/ferpa-password';
import { IHighSchoolTranscript } from 'src/app/shared/models/high-school-transcript.model';
import { IHold } from 'src/app/shared/models/hold.model';
import { IHousingData } from 'src/app/shared/models/housing-data.model';
import { IId } from 'src/app/shared/models/id.model';
import { LibertyStudentSummary } from 'src/app/shared/models/liberty-student-summary.model';
import { IPriorColleges } from 'src/app/shared/models/prior-college.model';
import { ISanctionSummary } from 'src/app/shared/models/sanction-summary.model';
import { IStudentProgramList } from 'src/app/shared/models/student-program-list.model';
import { IStudentStatus } from 'src/app/shared/models/student-status.model';
import { ITestScore } from 'src/app/shared/models/test-score.model';
import { ITest } from 'src/app/shared/models/test.model';
import { IBasePerson } from 'src/app/shared/people/models/person';
import { Cacheable } from 'ts-cacheable';
import { PeopleRepository } from '../repositories/people.repository';

@Injectable({
  providedIn: 'root'
})
export class PeopleService {

  constructor(private peopleRepo: PeopleRepository) { }

  @PopupHttpErrors()
  public search(criteria: string): Observable<Array<IBasePerson>> {
    return this.performCachedSearch(criteria);
  }

  @PopupHttpErrors()
  public findByPidm(pidm: number): Observable<IBasePerson> {
    return this.peopleRepo.findByPidm(pidm);
  }

  @PopupHttpErrors()
  public ferpa(pidm: number): Observable<Array<IFerpaPassword>> {
    return this.peopleRepo.ferpa(pidm);
  }

  @PopupHttpErrors()
  public query(criteria: string, withIncidents = false): Observable<Array<IBasePerson>> {
    return this.peopleRepo.query(criteria, withIncidents);
  }

  @PopupHttpErrors()
  public getBio(pidm:number): Observable<IBio> {
    return this.peopleRepo.getBio(pidm);
  }

  @PopupHttpErrors()
  public getCitizenship(pidm:number): Observable<ICitizenship> {
    return this.peopleRepo.getCitizenship(pidm);
  }


  @PopupHttpErrors()
  public concealedCarryStatus(pidm:number): Observable<IConcealedCarryStatus> {
    return this.peopleRepo.concealedCarryStatus(pidm);
  }


  @PopupHttpErrors({suppressPopupsForCodes: [400]})
  public searchWithBadRequestErrorsReturned(criteria: string): Observable<Array<IBasePerson>> {
    return this.performCachedSearch(criteria);
  }

  // Caches results for the most recent 20 unique searches
  @Cacheable({ maxCacheCount: 20 })
  private performCachedSearch(criteria: string): Observable<Array<IBasePerson>> {
    return this.peopleRepo.search(criteria);
  }

  @PopupHttpErrors()
  @Cacheable()
  public housing(pidm: number, term:string): Observable<IHousingData> {
    return this.peopleRepo.housing(pidm,term);
  }

  @PopupHttpErrors({suppressPopupsForCodes: [400]})
  @Cacheable({ maxCacheCount: 20 })
  public getAdmissionsApplication(pidm: number, term: string, appNum: string): Observable<IFullAdmissionsApplication> {
    return this.peopleRepo.findAdmissionsApplication(pidm, term, appNum);
  }

  @Cacheable({ maxCacheCount: 20 })
  public getAdmissionsApplications(pidm: number): Observable<Array<IAdmissionsApplication>> {
    return this.peopleRepo.findAdmissionsApplications(pidm);
  } 

  @PopupHttpErrors()
  @Cacheable()
  public getCurrentUser(): Observable<IBasePerson> {
    return this.peopleRepo.whoAmI();
  }

  @PopupHttpErrors()
  @Cacheable()
  public getStudentClassStatus(student: IBasePerson, termCode: string): Observable<IAcademicStatus> {
    return this.peopleRepo.findClassStatus(student, termCode);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getStudentStatus(student: IBasePerson, termCode?: string): Observable<IStudentStatus> {
    return this.peopleRepo.studentStatus(student, termCode);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getFciStatus(student: IBasePerson, termCode: string): Observable<IDescription> {
    return this.peopleRepo.getFciStatus(student, termCode);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getEmail(student: IBasePerson): Observable<IDescription> {
    return this.peopleRepo.getEmail(student.pidm);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getHousing(student: IBasePerson, termCode: string): Observable<IHousingData> {
    return this.peopleRepo.getHousing(student, termCode);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getPhone(student: IBasePerson, code: string): Observable<IDescription> {
    return this.peopleRepo.getPhone(student, code);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getAddress(student: IBasePerson, code: string): Observable<IAddress> {
    return this.peopleRepo.getAddress(student, code);
  }
  
  @PopupHttpErrors()
  @Cacheable()
  public getLuoaStudentPreviousOffenseCount(student: IBasePerson, termCode?: string): Observable<number> {
    return this.peopleRepo.findPreviousLuoaOffenseCount(student, termCode).pipe(
      map((response: IId) => response.id)
    );
  }
  
  @PopupHttpErrors()
  @Cacheable()
  public getStudentHolds(student: IBasePerson, termCode?: string): Observable<Array<IHold>> {
    return this.peopleRepo.findHolds(student, termCode);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getComments(pidm: number): Observable<Array<IComment>> {
    return this.peopleRepo.getComments(pidm);
  }

  @PopupHttpErrors()
  public postComment(pidm: number, comment: IComment): Observable<IId> {
    return this.peopleRepo.postComment(pidm, comment);
  }
  
  @PopupHttpErrors()
  @Cacheable()
  public getAcademicStatus(student: IBasePerson, termCode: string): Observable<IAcademicStatus> {
    return this.peopleRepo.findAcademicStatus(student, termCode);
  }

  public getProgramInformation(student: IBasePerson, termCode: string): Observable<IStudentProgramList> {
    return this.peopleRepo.getProgramInformation(student, termCode);
  }

  public getCourseHistory(pidm: number, levelCode: string, termCode: string): Observable<Array<IAuditCourseHistory>> {
    return this.peopleRepo.getCourseHistory(pidm, levelCode, termCode);
  }

  public getTestScores(pidm: number): Observable<Array<ITest>> {
    return this.peopleRepo.getTestScores(pidm);
  }

  @PopupHttpErrors()
  @Cacheable()
  public findSanctions(pidm: number, termCode: string, officeTypeId: number): Observable<ISanctionSummary> {
    return this.peopleRepo.findSanctions(pidm, termCode, officeTypeId);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getPriorColleges(pidm: number): Observable<IPriorColleges> {
    return this.peopleRepo.findPriorColleges(pidm);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getHighSchoolTranscripts(pidm: number): Observable<Array<IHighSchoolTranscript>> {
    return this.peopleRepo.findHighSchoolTranscripts(pidm);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getDismissedFromPreviousSchool(pidm: number, term: string, appNum: string): Observable<IAdmissionsQuestion> {
    return this.peopleRepo.findDismissedFromPreviousSchool(pidm, term, appNum);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getAllStudentInfo(pidm: number): Observable<Array<LibertyStudentSummary>> {
    return this.peopleRepo.findAllStudentInfo(pidm);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getAdmissionsTestScores(pidm: number): Observable<Array<ITestScore>> {
    return this.peopleRepo.getAdmissionsTestScores(pidm);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getAdmissionsDeposit(pidm: number, termCode: string): Observable<IAdmissionsDeposit> {
    return this.peopleRepo.getAdmissionsDeposit(pidm, termCode);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getEfc(pidm: number, termCode: string): Observable<IDescription> {
    return this.peopleRepo.getEfc(pidm, termCode);
  }

  @PopupHttpErrors()
  @Cacheable()
  public getAdvisors(pidm: number, termCode: string): Observable<Array<IAdvisor>> {
    return this.peopleRepo.getAdvisors(pidm, termCode);
  }

  @PopupHttpErrors()
  @Cacheable()
  public hasProgramDismissal(pidm: number): Observable<boolean> {
    return this.peopleRepo.hasProgramDismissal(pidm);
  }

}
